// AVS-4391: KVS TURN credentials expire (default Ttl 300s). These helpers decide WHEN the
// background refresh in rtc.ts must refetch /ice-servers so that every RTCPeerConnection —
// late joiners, rejoins, and AVS-4311 transport rebuilds included — is built from live
// credentials instead of a list that died five minutes into the call.

export type TIceServerWithTtl = RTCIceServer & { ttl?: number };

// KVS default TURN credential lifetime, assumed when the response carries no expiry signal
// (an older lambda that does not forward Ttl yet).
const DEFAULT_TTL_MS = 300_000;

// Refresh at 75% of the remaining lifetime: early enough to absorb one failed attempt and
// background-tab timer throttling (timers coalesce to minute boundaries) before expiry.
const REFRESH_FRACTION = 0.75;

// Floor guards against a client clock running ahead of the server-minted username epoch —
// a skewed client would otherwise compute a near-zero delay and refetch in a hot loop.
const MIN_REFRESH_DELAY_MS = 30_000;

// Ceiling for the explicit `ttl`, which is a RELATIVE lifetime and so immune to clock skew: only
// a corrupt or tampered value (ttl in the millions) can push the refresh past any real call and
// silently restore the AVS-4391 bug. An hour is well above the 300s KVS default, low enough that
// a bad value degrades to refreshing too often.
const MAX_TTL_MS = 3_600_000;

// Short fuse after a failed refresh: waiting a full cycle would leave the cached credentials
// dead for minutes; a 30s retry keeps the outage window close to the fetch failure itself.
export const RETRY_DELAY_MS = 30_000;

/**
 * When the credential list expires, in epoch ms — from the explicit `ttl` the lambda forwards
 * (AVS-4391), or, for an older lambda, from the epoch-seconds prefix KVS embeds in the TURN
 * username. Null when the list carries no expiry signal at all.
 *
 * AVS-4418: the lambda forwards EVERY KVS relay, each with its own credential and lifetime, so
 * the list is only as live as its shortest-lived entry — within each tier the EARLIEST expiry
 * wins, never the first one listed.
 */
export function getIceServersExpiry(iceServers: TIceServerWithTtl[], fetchedAtMs: number): number | null {
  const ttlExpiry = earliestExpiry(iceServers, (server) =>
    typeof server.ttl === 'number' && Number.isFinite(server.ttl)
      ? Math.min(fetchedAtMs + server.ttl * 1000, fetchedAtMs + MAX_TTL_MS)
      : null
  );
  if (ttlExpiry !== null) return ttlExpiry;

  return earliestExpiry(iceServers, (server) => {
    const epochMs = Number(server.username?.split(':')[0]) * 1000;
    if (!Number.isFinite(epochMs) || epochMs <= fetchedAtMs) return null;
    // The epoch is an ABSOLUTE server timestamp, so a client clock running behind KVS inflates
    // `epoch - now` by exactly the skew: a client 10 minutes slow reads a credential that dies
    // in 300s as having 900s left and schedules the refresh well after it is already dead. The
    // real lifetime can never exceed the KVS default, so the epoch may only SHORTEN that window
    // (credentials minted before our fetch), never lengthen it.
    return Math.min(epochMs, fetchedAtMs + DEFAULT_TTL_MS);
  });
}

/** The smallest expiry `expiryOf` yields across the list; null when it yields none. */
function earliestExpiry(
  iceServers: TIceServerWithTtl[],
  expiryOf: (server: TIceServerWithTtl) => number | null
): number | null {
  let earliest: number | null = null;
  for (const server of iceServers) {
    const expiry = expiryOf(server);
    if (expiry !== null && (earliest === null || expiry < earliest)) earliest = expiry;
  }
  return earliest;
}

/** Delay until the next background refresh should fire, measured from `nowMs`. */
export function getIceRefreshDelayMs(iceServers: TIceServerWithTtl[], fetchedAtMs: number, nowMs: number): number {
  const expiry = getIceServersExpiry(iceServers, fetchedAtMs) ?? fetchedAtMs + DEFAULT_TTL_MS;
  return Math.max((expiry - nowMs) * REFRESH_FRACTION, MIN_REFRESH_DELAY_MS);
}

/** Whether the cached list is already past its (known or assumed) expiry. */
export function areIceServersExpired(iceServers: TIceServerWithTtl[], fetchedAtMs: number, nowMs: number): boolean {
  const expiry = getIceServersExpiry(iceServers, fetchedAtMs) ?? fetchedAtMs + DEFAULT_TTL_MS;
  return nowMs >= expiry;
}

// AVS-4418: what the RTCPeerConnection constructor accepts. It THROWS on the first entry it
// rejects — no `urls` (TypeError: the `{}` an older lambda serialises when KVS returns nothing),
// a url that is not a stun/turn URL (SyntaxError), a turn/turns url without both credentials
// (InvalidAccessError) — and one throw costs the client every peer connection it will ever build.
const ICE_URL_SCHEME = /^(stun|stuns|turn|turns):/i;
const TURN_URL_SCHEME = /^turns?:/i;

function isIceUrl(value: unknown): value is string {
  return typeof value === 'string' && ICE_URL_SCHEME.test(value);
}

function isUsableIceServer(value: unknown): value is RTCIceServer {
  if (typeof value !== 'object' || value === null) return false;
  const { urls, username, credential } = value as Record<string, unknown>;
  const urlList: unknown[] = Array.isArray(urls) ? urls : [urls];
  const iceUrls = urlList.filter(isIceUrl);
  if (iceUrls.length === 0 || iceUrls.length !== urlList.length) return false;
  const needsCredentials = iceUrls.some((url) => TURN_URL_SCHEME.test(url));
  return !needsCredentials || (typeof username === 'string' && typeof credential === 'string');
}

/**
 * The entries of a fetched ICE server list that the browser will accept — same object references,
 * same order — so the shapes the RTCPeerConnection constructor is known to refuse never reach it
 * (AVS-4418); AVS-4430's catch around the constructor stays the backstop for anything else. A
 * payload that is not an array yields an empty list rather than a throw. The caller decides what
 * to say about what was dropped.
 */
export function filterUsableIceServers(iceServers: unknown): RTCIceServer[] {
  return Array.isArray(iceServers) ? iceServers.filter(isUsableIceServer) : [];
}
